/**
 * What a wheel notch over a terminal means.
 *
 * Separated from the pane because the interesting case cannot be reached from a
 * test that mounts one: the answer turns on `buffer.active.type`, and a pane
 * only reaches the alternate screen when a real tmux client has written to it.
 * A browser test with a stubbed socket sits on the normal buffer forever, so a
 * test written there passes whatever this decides — which is how the old
 * behaviour would have survived a rewrite that was supposed to remove it.
 *
 * The three answers, and why:
 *
 *   - **`record`** — the operator held Cmd. Answered before anything else, on
 *     either buffer: with the modifier this is a request for the session's own
 *     record, not for the ten thousand lines this emulator happens to hold.
 *   - **`scroll`** — a normal buffer with nothing tracking the mouse has real
 *     scrollback, and xterm can move its own viewport. Never gated on the input
 *     lease: scrolling must work for somebody who is only watching.
 *   - **`swallow`** — the alternate screen, and a normal buffer under an
 *     application tracking the mouse itself. The second reads the wheel and
 *     would be desynchronised by the viewport moving under it. The first is
 *     every agent pane: tmux drives the alternate screen (ADR-0010) and xterm
 *     keeps no scrollback there, so it turns each notch into a cursor-key
 *     sequence and sends it as *input* — for the controller the gesture walks
 *     the agent's prompt history, and for an observer it is dropped at the lease
 *     gate and does nothing at all. Neither is scrolling. So the notch is
 *     dropped: it used to open the record, and a two-finger drift over a wall
 *     somebody was reading is not a request for a full-height overlay over a
 *     live terminal.
 */
export type WheelVerdict = 'record' | 'scroll' | 'swallow'

export function wheelVerdict(ev: {
  /** Cmd (Meta). The one modifier plane a terminal has no sequence for. */
  meta: boolean
  /** Whether the emulator is on the alternate screen. */
  altScreen: boolean
  /** Whether the application is tracking the mouse itself. */
  mouseTracking: boolean
}): WheelVerdict {
  if (ev.meta) return 'record'
  if (!ev.altScreen && !ev.mouseTracking) return 'scroll'
  return 'swallow'
}
